import { Card, Grid, makeStyles, Typography } from '@material-ui/core'
import PromotionPreview, {
  PROMOTION_VIEW_MODE,
} from 'app/components/dealPromotion/PromotionPreview'
import ImageWithFallback from 'app/components/ImageWithFallback'
import NotificationTypePreview from 'app/components/notificationBadge/NotificationTypePreview'
import RichTextDisplay from 'app/components/richTextEditor/RichTextDisplay'
import Topic from 'app/components/Topic'
import {
  default as assetConfigs,
  default as Assets,
} from 'app/configs/assetConfigs'
import {
  TYPE_EVENT,
  TYPE_GENERAL,
  TYPE_VOUCHER,
  TYPE_POLL,
} from 'app/constants/messageType'
import Radio from '@material-ui/core/Radio'
import RadioGroup from '@material-ui/core/RadioGroup'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import apiConfig from 'app/configs/apiConfigs'
import { normalizeEmptyParagraphs } from 'app/main/composeMessage/tiptapEditor/TiptapUtils'
import clsx from 'clsx'
import useMessageExpiration from 'commons/useMessageExpiration'
import PropTypes from 'prop-types'
import * as R from 'ramda'
import { FormattedMessage, useIntl } from 'react-intl'
import NotificationImportantPreview from '../../../../components/notificationBadge/NotificationImportantPreview'
import NotificationEventAction from './NotificationEventAction'
import NotificationEventDate from './NotificationEventDate'
import NotificationEventPlace from './NotificationEventPlace'
import NotificationMediaPreview from './NotificationMediaPreview'
import NotificationPreviewActions from './NotificationPreviewActions'
import NotificationPropTypes from './NotificationPropTypes'

const useStyles = makeStyles((theme) => ({
  previewStyle: {
    borderRadius: 0,
    margin: '1rem',
  },
  placeName: { color: theme.palette.secondary.main },
  placeLogoImage: {
    objectFit: 'cover',
    objectPosition: 'center',
    overflow: 'hidden',
    borderRadius: '50%',
  },
  directionRtl: {
    direction: 'rtl',
  },
  textColorBlur: {
    fontSize: '12px',
    fontWeight: 'bold',
    fontStretch: 'normal',
    fontStyle: 'normal',
    color: '#247bcd',
  },
  dotColor: {
    color: '#00314b',
  },
  voucherTicket: {
    position: 'relative',
    width: '100%',
  },
  voucherTicketText: {
    display: 'flex',
    flexDirection: 'column',
    position: 'absolute',
    top: '30%',
    left: '10%',
  },
  ticketTitle: {
    fontStyle: 'italic',
    fontSize: '14px',
    display: 'block',
    lineHeight: '1.14',
  },
  ticketSubtitle: {
    fontSize: '18px',
    display: 'block',
    lineHeight: '1.14',
  },
  title_category_1: {
    color: '#FFF',
  },
  subtitle_category_1: {
    color: '#006296',
  },
  title_category_2: {
    color: '#444',
  },
  subtitle_category_2: {
    color: '#950',
  },
  title_category_3: {
    color: '#444',
  },
  subtitle_category_3: {
    color: '#444',
  },
  title_category_4: {
    color: '#FF9',
  },
  subtitle_category_4: {
    color: '#FFF',
  },
  title_category_5: {
    color: '#444',
  },
  subtitle_category_5: {
    color: '#F79',
  },
  formLabel: {
    '& .MuiFormControlLabel-label': {
      width: '100%',
      color: '#000000 !important',
    },
  },
}))

const NotificationPreview = ({ notification, isInitialNotification }) => {
  const { formatMessage } = useIntl()
  const classes = useStyles()
  const banners = assetConfigs.dealAndPromotion.banners
  const placeTopics = R.prop('topics', notification)

  const getPromotionDetail = (notification) => {
    return {
      title: R.prop('voucherTitle', notification),
      desc: R.prop('voucherDescription', notification),
      endTime: R.prop('endDateTime', notification.voucher),
      startTime: R.prop('startDateTime', notification.voucher),
    }
  }

  const messageExpiration = useMessageExpiration(notification)
  const isMessageExpiration = R.and(
    messageExpiration.hasExpiration,
    !isInitialNotification,
  )

  const isPoll = R.propEq('type', TYPE_POLL, notification)
  const isNotNilOrEmpty = (value) => {
    return !R.isNil(value) && !R.isEmpty(value) && value.trim() !== ''
  }

  const showOptionPoll = (options) => {
    if (!options || !Array.isArray(options)) return null
    return options
      .filter((item) => item && isNotNilOrEmpty(item.title))
      .map((item, index) => (
        <RadioGroup className="w-full" key={`option-${index + 1}`}>
          <FormControlLabel
            value={item.title}
            className={classes.formLabel}
            control={<Radio disabled />}
            label={
              <div className="flex justify-between w-full">
                <Typography className="flex-1 my-auto">{item.title}</Typography>
                <div className="p-2 items-center flex">
                  {isNotNilOrEmpty(item.imagePath) ? (
                    <img
                      src={`${apiConfig.cndUrl}/${item.imagePath}`}
                      alt={item.title}
                      className="w-44 h-44 rounded-8 border-1 border-gray-300"
                    />
                  ) : (
                    <div className="w-44 h-44 rounded-8 bg-gray-200 border-1 border-gray-300"></div>
                  )}
                </div>
              </div>
            }
          />
        </RadioGroup>
      ))
  }

  if (notification) {
    const titleWithRichText = (
      <RichTextDisplay text={R.prop('title', notification)} />
    )
    return (
      <Card
        className={clsx(classes.previewStyle, 'w-11/12 sm:w-400 sm:max-w-400')}
        elevation={5}
      >
        <div className="flex-col min-w-xs">
          <div className="w-full inline-flex py-4">
            <div className="flex w-1/4 max-w-64 max-h-64 ltr:pl-16 rtl:pr-16 p-8 items-center">
              <ImageWithFallback
                className={classes.placeLogoImage}
                alt="logo"
                src={R.prop('placeLogo', notification)}
                fallbackSrc={Assets.defaultPlaceImage}
              />
            </div>
            <div className="flex-shrink flex-col w-3/4 justify-center py-12 ltr:pr-8 rtl:pl-8 ltr:pl-4 rtl:pr-4">
              <Typography className={clsx(classes.placeName, 'truncate')}>
                <b>{R.prop('placeName', notification)}</b>
              </Typography>
              <Typography>
                {formatMessage({ id: 'notificationPreview.placeholderTime' })}
              </Typography>
              <div className="flex mt-2">
                <NotificationTypePreview
                  className="ltr:mr-6 rtl:ml-6"
                  type={R.prop('type', notification)}
                />
                <NotificationImportantPreview
                  priority={R.prop('priority', notification)}
                  className="ltr:ml-8 rtl:mr-8"
                />
              </div>
            </div>
          </div>
          {isMessageExpiration && (
            <div className="w-full my-8 border-t-1 border-b-1 border-gray-500">
              <div className="w-full px-16 py-16 flex items-center">
                <svg
                  className={clsx(
                    classes.expirationIcon,
                    'h-16 w-16 fill-current',
                  )}
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                >
                  <path fill="none" d="M0 0h24v24H0z" />
                  <path d="M15 2h-3.5l-1-1h-5l-1 1H1v2h14zM16 9c-.7 0-1.37.1-2 .29V5H2v12c0 1.1.9 2 2 2h5.68A6.999 6.999 0 0 0 23 16c0-3.87-3.13-7-7-7zm0 12c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z" />
                  <path d="M16.5 12H15v5l3.6 2.1.8-1.2-2.9-1.7z" />
                </svg>
                <span className={clsx(classes.expirationDate, 'ml-8 text-xs')}>
                  {messageExpiration.expirationDatePreview}
                </span>
              </div>
            </div>
          )}
          <div className="w-full">
            {R.includes(notification.type, [TYPE_VOUCHER]) ? (
              <>
                <PromotionPreview
                  mode={PROMOTION_VIEW_MODE.PREVIEW}
                  promotionData={getPromotionDetail(notification)}
                />
              </>
            ) : (
              <NotificationMediaPreview
                media={R.prop('media', notification)}
                type={R.prop('type', notification)}
              />
            )}
          </div>
          {R.includes(notification.type, [TYPE_GENERAL]) && (
            <div dir="auto" className="w-full pb-8 px-16">
              <div className="break-words font-bold">{titleWithRichText}</div>
            </div>
          )}
          {R.propEq('type', TYPE_EVENT, notification) && (
            <>
              <NotificationEventDate
                startDateTime={R.path(['event', 'startDateTime'], notification)}
                startTimezone={R.path(['event', 'startTimezone'], notification)}
                endDateTime={R.path(['event', 'endDateTime'], notification)}
                endTimezone={R.path(['event', 'endTimezone'], notification)}
              />
              <NotificationEventPlace
                title={titleWithRichText}
                placeName={R.path(['event', 'location'], notification)}
              />
              <NotificationEventAction />
            </>
          )}

          {isPoll && (
            <div dir="auto" className="w-full pb-8 px-16">
              <div className="break-words pt-8">
                <Typography className="text-16 font-bold mb-4">
                  {R.prop('question', notification) || ''}
                </Typography>
                {R.prop('options', notification) &&
                  Array.isArray(R.prop('options', notification)) &&
                  R.prop('options', notification).length > 0 && (
                    <div className="py-2">
                      {showOptionPoll(R.prop('options', notification))}
                    </div>
                  )}
              </div>
            </div>
          )}

          {!isPoll && (
            <div dir="auto" className="w-full pb-8 px-16">
              <div className="break-words pt-8">
                <RichTextDisplay
                  text={normalizeEmptyParagraphs(R.prop('body', notification))}
                />
              </div>
            </div>
          )}

          {R.prop('voucher', notification) && (
            <div className="relative w-full my-20">
              <ImageWithFallback
                alt="ticket"
                src={
                  banners[
                    `category_${R.path(['voucher', 'category'], notification)}`
                  ]
                }
                fallbackSrc={Assets.dealAndPromotion.voucherTicket}
              />
              <div className={classes.voucherTicketText}>
                <Typography
                  variant="button"
                  className={clsx(
                    classes.ticketTitle,
                    classes[
                      `title_category_${R.path(
                        ['voucher', 'category'],
                        notification,
                      )}`
                    ],
                  )}
                >
                  <FormattedMessage id="createNotification.preview.dialog.voucher.ticket.title" />
                </Typography>
                <Typography
                  variant="button"
                  className={clsx(
                    classes.ticketSubtitle,
                    classes[
                      `subtitle_category_${R.path(
                        ['voucher', 'category'],
                        notification,
                      )}`
                    ],
                  )}
                >
                  <FormattedMessage id="createNotification.preview.dialog.voucher.ticket.subtitle" />
                </Typography>
              </div>
            </div>
          )}

          {/* Add List Topics */}
          {!R.isEmpty(placeTopics) && (
            <Grid container className="w-full pb-8 px-16">
              {placeTopics.map((topic, index) => {
                return (
                  <div className="mr-2" key={index}>
                    <Topic id={topic.place_id} name={topic.name} />
                  </div>
                )
              })}
            </Grid>
          )}
          <NotificationPreviewActions notification={notification} />
        </div>
      </Card>
    )
  } else return null
}

NotificationPreview.propTypes = {
  notification: PropTypes.shape(NotificationPropTypes),
}

export default NotificationPreview
